import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Avatar,
  alpha,
} from '@mui/material';
import {
  DispositivoRede,
  DispositivoRedeTipo,
  DISPOSITIVO_REDE_TIPOS,
} from '../../../services/dispositivoRedeService';

export const STATUS_COLORS = {
  online: '#4CAF50',
  offline: '#F44336',
  unknown: '#9E9E9E',
} as const;

/** Intervalo de atualização do diagrama/lista (alinhado ao PING_INTERVAL_MS do monitor-service). */
export const REDE_MONITOR_POLL_MS = 5000;

export const FILTER_OPTIONS = [
  { key: 'todos', label: 'Todos', color: undefined },
  { key: 'online', label: 'Online', color: STATUS_COLORS.online },
  { key: 'offline', label: 'Offline', color: STATUS_COLORS.offline },
  { key: 'unknown', label: 'Desconhecido', color: STATUS_COLORS.unknown },
] as const;

export type StatusFilter = 'todos' | 'online' | 'offline' | 'unknown';

export function a11yProps(index: number) {
  return {
    id: `monitoramento-rede-tab-${index}`,
    'aria-controls': `monitoramento-rede-tabpanel-${index}`,
  };
}

export const getTipoLabel = (tipo: DispositivoRedeTipo) =>
  DISPOSITIVO_REDE_TIPOS.find((t) => t.value === tipo)?.label ?? tipo;

export const TIPOS_COM_MONITOR_LATENCIA: DispositivoRedeTipo[] = [
  'servidor',
  'roteador',
  'radio_ap',
  'radio_station',
  'cliente',
  'servicos',
];

export function shouldShowMonitorLatencia(tipo: DispositivoRedeTipo | string): boolean {
  return (TIPOS_COM_MONITOR_LATENCIA as readonly string[]).includes(tipo);
}

export function formatMonitorLatenciaMs(latenciaMs?: number | null): string | null {
  if (latenciaMs == null) return null;
  return `${latenciaMs} ms`;
}

export function getMonitorLatenciaExibicao(
  tipo: DispositivoRedeTipo | string,
  latenciaMs?: number | null
): string | null {
  if (!shouldShowMonitorLatencia(tipo)) return null;
  return formatMonitorLatenciaMs(latenciaMs);
}

export const getIpExibicao = (dispositivo: DispositivoRede): string => {
  if (dispositivo.endereco_ip) return dispositivo.endereco_ip;
  if (dispositivo.tipo === 'cliente' && dispositivo.contrato_servico?.endereco_ip) {
    return dispositivo.contrato_servico.endereco_ip;
  }
  if (dispositivo.tipo === 'cliente') {
    return 'RADIUS (dinâmico)';
  }
  return '—';
};

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

export const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon, active, onClick }) => (
  <Card
    elevation={active ? 4 : 0}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      height: '100%',
      border: '2px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: active ? alpha(color || '#2196F3', 0.08) : 'background.paper',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3,
        borderColor: color || 'primary.main',
      },
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {label}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ color: color || 'primary.main', lineHeight: 1.2, mt: 0.5 }}
          >
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(color || '#2196F3', 0.12),
            color: color || 'primary.main',
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);
